import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  TextField,
  DatePicker,
  Checkbox,
  Spinner,
  SpinnerSize,
  ContextualMenu,
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { ProyectoGlosaSavePayload } from '../../system/proyectos_glosa/ProyectosGlosaSystem';
import { validateProyectosGlosa, hasErrors, ProyectosGlosaFormValues, ProyectosGlosaErrors } from './ProyectosGlosaValidation';
import { getLocalizedStrings } from '../../lib/LocalizedStringsForDatePicker';

const API_DATE_FORMAT = 'dd/MM/yyyy';

const parseApiDate = (dateStr: string): Date | undefined => {
  if (!dateStr) return undefined;
  const dt = DateTime.fromFormat(dateStr, API_DATE_FORMAT);
  return dt.isValid ? dt.toJSDate() : undefined;
};

const formatDateStart = (date: Date | null | undefined): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
};

const formatDateEnd = (date: Date | null | undefined): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
};

interface ProyectosGlosaEditDialogProps {
  show: boolean;
  itemId: string;
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
  onClose: () => void;
  onSaved: () => void;
}

const ProyectosGlosaEditDialog: React.FC<ProyectosGlosaEditDialogProps> = (props) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ProyectosGlosaFormValues>({
    dicprog_nombre_proyecto: '',
    dicprog_fecha_validez_inicio: undefined,
    dicprog_fecha_validez_fin: undefined,
    dicprog_activo: true,
  });
  const [errors, setErrors] = useState<ProyectosGlosaErrors>({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (props.show && props.itemId) {
      loadRecord(props.itemId);
    }
  }, [props.show, props.itemId]);

  const loadRecord = async (recordId: string) => {
    setIsLoaded(false);
    props.changeBlockUI(true);
    try {
      const record = await props.system.getProyectosGlosaSystem().getById(recordId);
      setFormData({
        dicprog_nombre_proyecto: record.dicprog_nombre_proyecto,
        dicprog_fecha_validez_inicio: parseApiDate(record.dicprog_fecha_validez_inicio),
        dicprog_fecha_validez_fin: parseApiDate(record.dicprog_fecha_validez_fin),
        dicprog_activo: record.dicprog_activo,
      });
      setIsLoaded(true);
    } catch {
      props.courier.messageError(t('Error al cargar el registro'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateProyectosGlosa(formData);
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      return;
    }

    props.changeBlockUI(true);
    try {
      const payload: ProyectoGlosaSavePayload = {
        dicprog_nombre_proyecto: formData.dicprog_nombre_proyecto,
        dicprog_fecha_validez_inicio: formatDateStart(formData.dicprog_fecha_validez_inicio),
        dicprog_fecha_validez_fin: formatDateEnd(formData.dicprog_fecha_validez_fin),
        dicprog_activo: formData.dicprog_activo,
        dicprog_id: props.itemId,
      };

      await props.system.getProyectosGlosaSystem().save(payload);
      props.courier.messageSuccess(t('Registro actualizado exitosamente'));
      props.onSaved();
      props.onClose();
    } catch {
      props.courier.messageError(t('Error al guardar el registro'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  const updateField = (field: keyof ProyectosGlosaFormValues, value: string | boolean | Date | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <Dialog
      hidden={!props.show}
      onDismiss={props.onClose}
      dialogContentProps={{
        type: DialogType.normal,
        title: t('Editar Proyecto Glosa'),
      }}
      modalProps={{
        isBlocking: true,
        dragOptions: {
          moveMenuItemText: 'Move',
          closeMenuItemText: 'Close',
          menu: ContextualMenu,
        },
        styles: {
          main: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
          },
          scrollableContent: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            overflowY: 'auto',
          },
        },
      }}
      minWidth={500}
    >
      {!isLoaded ? (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Spinner size={SpinnerSize.large} />
        </div>
      ) : (
        <div style={{ padding: '12px 0' }}>
          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-lg12">
                <TextField
                  label={t('Nombre del proyecto')}
                  value={formData.dicprog_nombre_proyecto}
                  onChange={(e, val) => updateField('dicprog_nombre_proyecto', val || '')}
                  errorMessage={errors.dicprog_nombre_proyecto}
                />
              </div>
            </div>
            <div className="ms-Grid-row mt-2">
              <div className="ms-Grid-col ms-lg6">
                <DatePicker
                  label={t('Inicio de vigencia')}
                  value={formData.dicprog_fecha_validez_inicio}
                  onSelectDate={(date) => updateField('dicprog_fecha_validez_inicio', date || undefined)}
                  formatDate={(date) => (date ? DateTime.fromJSDate(date).toFormat('dd/MM/yyyy') : '')}
                  strings={getLocalizedStrings()}
                />
                {errors.dicprog_fecha_validez_inicio && (
                  <div style={{ color: '#a4262c', fontSize: 12, marginTop: 5 }}>{errors.dicprog_fecha_validez_inicio}</div>
                )}
              </div>
              <div className="ms-Grid-col ms-lg6">
                <DatePicker
                  label={t('Fin de vigencia')}
                  value={formData.dicprog_fecha_validez_fin}
                  onSelectDate={(date) => updateField('dicprog_fecha_validez_fin', date || undefined)}
                  formatDate={(date) => (date ? DateTime.fromJSDate(date).toFormat('dd/MM/yyyy') : '')}
                  strings={getLocalizedStrings()}
                />
                {errors.dicprog_fecha_validez_fin && (
                  <div style={{ color: '#a4262c', fontSize: 12, marginTop: 5 }}>{errors.dicprog_fecha_validez_fin}</div>
                )}
              </div>
            </div>
            <div className="ms-Grid-row mt-2">
              <div className="ms-Grid-col ms-lg12">
                <Checkbox label={t('Activo')} checked={formData.dicprog_activo} onChange={(e, checked) => updateField('dicprog_activo', checked)} />
              </div>
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <PrimaryButton onClick={handleSave} text={t('Guardar')} disabled={!isLoaded} />
        <DefaultButton onClick={props.onClose} text={t('Cancelar')} />
      </DialogFooter>
    </Dialog>
  );
};

export default ProyectosGlosaEditDialog;
